/* eslint-disable max-lines-per-function */
import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { Box, Checkbox, FormLabel, Select, Stack, Text } from '@chakra-ui/react';
import { COUNTERTOP_MINIMUM_OVERHANG, COUNTERTOP_NONE_KEY, Countertops, Island, Kitchen, useSubscription } from '@rta/rta-blueprint-library';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { displayDropdowns } from './helpers';
import { InlineNumberInput } from './controls';
import { lang } from '@lang';
import { useDesign } from '@hooks';

export type CountertopPropertiesPanelProps = {
	id: string | null;
};

export const CountertopPropertiesPanel = ({ id }: CountertopPropertiesPanelProps): JSX.Element => {
	const [controller] = useDesign<BpController>();

	const [island, setIsland] = useState<Island | null>(null);
	const [kitchen, setKitchen] = useState<Kitchen | null>(null);
	const readonly = controller?.readonly ?? true;

	useSubscription(controller?.kitchen ?? null, false, [
		['countertop.*', '*'],
		['panel.*', '*']
	]);

	useEffect(() => {
		if (controller && id) {
			const refMap = controller?.kitchen?.getReferenceMap(id);
			// A Countertop's parent is the Countertops collection, not an Island (one countertop can span multiple islands),
			// so refMap.island is null when id is a countertop id -- resolve the island via the countertop's islandIds instead,
			// otherwise the panel silently falls back to editing the kitchen-wide default material instead of this countertop
			const resolvedIsland = refMap.island ?? (refMap.countertop
				? controller?.kitchen?.design?.islands?.findById(refMap.countertop.islandIds[0] ?? '') ?? null
				: null);
			setIsland(resolvedIsland);
			setKitchen(refMap.kitchen);
		} else if (controller) {
			// No countertop clicked -- fall back to the island currently being viewed
			const viewedIslandId = controller.navigation?.islandId ?? '';
			setIsland(controller.kitchen?.design?.islands?.findById(viewedIslandId) ?? null);
			setKitchen(controller.kitchen);
		}
	}, [controller, id, controller?.navigation?.islandId]);

	if (!kitchen) return <></>;

	const countertopAssetData = Countertops.assetData;
	const countertop = island ? island.getCountertop().material : kitchen?.material.countertop ?? null;
	const isNoneCountertop = countertop?.key === COUNTERTOP_NONE_KEY;
	const showGeometryControls = !isNoneCountertop;
	// console.log('render:countertop-properties-panel', id, { countertop, island });

	return <ContextPanelContent title={lang.contextPanels.title.countertopProperties}>
		<>
			<Box marginTop="5px" marginBottom={3} padding={2} textAlign="left" borderLeft="1px solid grey" borderBottom="1px solid darkgrey">
				<Stack spacing="2" width="100%" height="65%">
					<Box>
						{ island &&
							<Checkbox id="countertop-material-override" defaultChecked={ countertop?.override }
								disabled={readonly}
								onChange={(e) => {
									if (countertop) {
										countertop.override = e.target.checked;
									}
								}}>
								<Text fontSize="small">Override Kitchen Settings</Text>
							</Checkbox>
						}
						{ countertop?.override
							? <Text fontSize="x-small">The Countertop setting shown here only apply to the selected countertop.</Text>
							: <Text fontSize="x-small">The countertop setting shown here apply to all kitchen countertops.</Text>
						}
					</Box>
					<Box>
						<FormLabel htmlFor="countertop-material" marginBottom={0} fontSize="small">Material:</FormLabel>
						<Select id="countertop-material" placeholder="Countertop" fontSize="small"
							value={countertop?.key}
							disabled={readonly}
							onChange={(e) => {
								if (countertop) {
									countertop.setMaterial(e.target.value);
								}
							}}>
							{displayDropdowns(countertopAssetData)}
						</Select>
					</Box>
					<Box>
						<InlineNumberInput
							category="countertop"
							label="Thickness"
							value={countertop?.thickness}
							enabled={!readonly}
							onChange={(valueString) => {
								if (countertop) {
								countertop.thickness = parseFloat(valueString);
								}
							}}
						/>
					</Box>
					{showGeometryControls && <Box>
						<FormLabel htmlFor="countertop-edge" marginBottom={0} fontSize="small">Edge:</FormLabel>
						<Select id="countertop-edge" placeholder="Edge" fontSize="small"
							value={countertop?.edge ? countertop?.edge : 'na'}
							disabled
							// onChange={(_e) => {
							// 	if (countertop) {
							// 		// Edge is read only property
							// 		// countertop.material.edge = e.target.value
							// 	}
							// }}
						>
							<option value="na">Not Applicable</option>
							<option value="clean">Clean</option>
							<option value="chiseled">Chiseled</option>
						</Select>
					</Box>}
				</Stack>
			</Box>

			{ island &&
				<Box marginBottom={3} padding={2} textAlign="left" borderLeft="1px solid grey" borderBottom="1px solid darkgrey">
					<Text paddingBottom={3} fontSize="medium">Overhangs</Text>
					<Stack spacing="3" width="100%" height="65%">
						{ island.panels.children.map((panel, i) => (
							<InlineNumberInput key={i}
								category="overhangs" label={panel.label}
								min={COUNTERTOP_MINIMUM_OVERHANG}
								value={panel.countertopOverhang}
								enabled={!readonly}
								onChange={(valueString) => {
									panel.countertopOverhang = parseFloat(valueString);
								}}
							/>
						))}
					</Stack>
				</Box>
			}
		</>
	</ContextPanelContent>;
};
